import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import 'chartjs-plugin-datalabels';
import { LastLocationProvider } from 'react-router-last-location';
import GuidelinePreviewPage from 'pages/GuidelinePreview';
import { AppRoutes } from 'consts/routesPath';
import Security from 'components/Security';
import VersionService from 'services/VersionService';

//* Add plugins to app
import './plugins/highlightTextJQuery';
import ForgotPassword from 'pages/ForgotPassword';
import ResetPassword from 'pages/ResetPassword';

import './App.scss';
import './index.css';
import 'react-medium-image-zoom/dist/styles.css';
import Loading from 'components/Loading';
import ScrollToTop from 'components/ScrollToTop';
import LoginLayout from 'layout/LoginLayout';
import InitPageLayout from 'layout/InitPageLayout';
import ThemeProvider from 'providers/ThemeProvider';
import ChartProvider from 'providers/ChartProvider';
import GlobalHooks from 'hooks/GlobalHooks';
import WebConfigsProvider from 'providers/WebConfigsProvider';

import PreventUserHook from 'hooks/PreventUserHook';
import WarningUpdateBillingInfoPage from 'pages/Warning/WarningUpdateBillingInfoPage';
import WarningMinBalancePage from 'pages/Warning/WarningMinBalancePage';
import { NotificationContainer } from 'react-notifications';
import 'react-tooltip/dist/react-tooltip.css';

import { Toaster } from 'sonner';
import { TIME_OUT_CLOSE_TOAST } from 'consts/data';
import DocumentPage from 'pages/Document';
import ColumnsHandlerProvider from 'providers/ColumnsHandlerProvider';
import TimezoneProvider from 'providers/TimezoneProvider';

const loading = () => (
  <div className='pt-3 text-center'>
    <Loading />
  </div>
);

// Layout
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'));

console.log(process.env.NODE_ENV);

// Pages
const Login = React.lazy(() => import('./pages/Login'));
const Page429 = React.lazy(() => import('./pages/Page429'));

console.log(`%cVersion ${VersionService.getVersion()}`, 'color: green');

const App = () => {
  return (
    <WebConfigsProvider>
      <InitPageLayout>
        <ThemeProvider>
          <ChartProvider>
            <Toaster
              richColors
              closeButton
              position='top-right'
              toastOptions={{
                duration: TIME_OUT_CLOSE_TOAST,
              }}
            />
            <Router>
              <ColumnsHandlerProvider>
                <LastLocationProvider>
                  <NotificationContainer />
                  <ScrollToTop />
                  <Suspense fallback={loading()}>
                    <Switch>
                      <Route
                        path={'/docs'}
                        name='Documents'
                        render={(props) => <DocumentPage {...props} />}
                      />
                      <Route
                        path={AppRoutes.Page429}
                        name='420 Too many requests'
                        render={(props) => <Page429 {...props} />}
                      />
                      <Route
                        path={AppRoutes.Login}
                        name='Login Page'
                        render={(props) => (
                          <LoginLayout>
                            <Login {...props} />
                          </LoginLayout>
                        )}
                      />
                      <Route
                        path={AppRoutes.ForgotPassword}
                        name='Forgot Password'
                        render={(props) => (
                          <LoginLayout>
                            <ForgotPassword {...props} />
                          </LoginLayout>
                        )}
                      />
                      <Route
                        path={AppRoutes.ResetPassword}
                        name='Reset Password'
                        render={(props) => (
                          <LoginLayout>
                            <ResetPassword {...props} />
                          </LoginLayout>
                        )}
                      />

                      <Security>
                        <TimezoneProvider>
                          <GlobalHooks />
                          <PreventUserHook />

                          <Switch>
                            <Route
                              path={AppRoutes.Guide}
                              name='Guide'
                              render={(props) => <GuidelinePreviewPage {...props} />}
                            />

                            <Route
                              path={AppRoutes.WarningUpdateBillingInfo}
                              name='Update Billing Info Warning'
                              render={(props) => {
                                return <WarningUpdateBillingInfoPage {...props} />;
                              }}
                            />

                            <Route
                              path={AppRoutes.WarningMinBalance}
                              name='Min Balance Warning'
                              render={(props) => {
                                return <WarningMinBalancePage {...props} />;
                              }}
                            />

                            <Route
                              path={AppRoutes.HomePage}
                              name='Home'
                              render={(props) => <DefaultLayout {...props} />}
                            />
                          </Switch>
                        </TimezoneProvider>
                      </Security>
                    </Switch>
                  </Suspense>
                </LastLocationProvider>
              </ColumnsHandlerProvider>
            </Router>
          </ChartProvider>
        </ThemeProvider>
      </InitPageLayout>
    </WebConfigsProvider>
  );
};

export default App;
